import React, {
    createContext, useContext, useCallback, useEffect, useState, useMemo,
} from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { TUTORIAL_STORAGE_KEY, TutorialStatus } from '../common/constants';
import { getTutorialSteps } from './steps';

interface TutorialContextValue {
  status: TutorialStatus;
  startTutorial: () => void;
  resetTutorial: () => void;
}

const TutorialContext = createContext<TutorialContextValue>({
    status: TutorialStatus.NOT_STARTED,
    startTutorial: () => {},
    resetTutorial: () => {},
});

export const useTutorial = () => useContext(TutorialContext);

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [status, setStatus] = useState<TutorialStatus>(() => {
        const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY);
        return (stored as TutorialStatus) || TutorialStatus.NOT_STARTED;
    });

    const startTutorial = useCallback(() => {
        setStatus(TutorialStatus.IN_PROGRESS);
        localStorage.setItem(TUTORIAL_STORAGE_KEY, TutorialStatus.IN_PROGRESS);

        const driverObj = driver({
            showProgress: true,
            steps: getTutorialSteps(),
            nextBtnText: 'Siguiente',
            prevBtnText: 'Anterior',
            doneBtnText: 'Finalizar',
            progressText: '{{current}} de {{total}}',
            onDestroyed: () => {
                setStatus(TutorialStatus.COMPLETED);
                localStorage.setItem(TUTORIAL_STORAGE_KEY, TutorialStatus.COMPLETED);
            },
            onCloseClick: () => {
                setStatus(TutorialStatus.SKIPPED);
                localStorage.setItem(TUTORIAL_STORAGE_KEY, TutorialStatus.SKIPPED);
                driverObj.destroy();
            },
        });

        // Small delay to ensure DOM elements are rendered
        setTimeout(() => {
            driverObj.drive();
        }, 500);
    }, []);

    const resetTutorial = useCallback(() => {
        localStorage.removeItem(TUTORIAL_STORAGE_KEY);
        setStatus(TutorialStatus.NOT_STARTED);
    // Will trigger the useEffect below
    }, []);

    useEffect(() => {
        if (status === TutorialStatus.NOT_STARTED) {
            // Auto-start on first visit after a small delay for DOM to render
            const timer = setTimeout(() => {
                startTutorial();
            }, 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [status, startTutorial]);

    const value = useMemo(() => ({ status, startTutorial, resetTutorial }), [status, startTutorial, resetTutorial]);

    return (
        <TutorialContext.Provider value={value}>
            {children}
        </TutorialContext.Provider>
    );
};
